import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Dialog, Input, Button } from '@/design-system';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { TEMPLATE_MAX_TAGS } from '@/contracts/templates';
import type { TemplateSummary } from '@/contracts/templates';

export interface EditCustomTemplateDialogProps {
  template: TemplateSummary | null;
  onClose: () => void;
  onSaved: () => void;
}

function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, TEMPLATE_MAX_TAGS);
}

/** SPEC-010 §58/AC-044 — the only editable custom-template fields: name/description/tags. */
export function EditCustomTemplateDialog({ template, onClose, onSaved }: EditCustomTemplateDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!template) return;
    setName(template.name);
    setDescription(template.description);
    setTagsInput(template.tags.join(', '));
    setError(null);
  }, [template]);

  if (!template) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!template) return;
    setSubmitting(true);
    setError(null);
    try {
      await dashboardApi.updateCustomTemplate(template.id, {
        name: name.trim(),
        description: description.trim(),
        tags: parseTags(tagsInput),
      });
      onClose();
      onSaved();
    } catch {
      setError('Não foi possível salvar as alterações. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={Boolean(template)} onClose={onClose} title="Editar modelo">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input label="Nome" value={name} onChange={(e) => setName(e.target.value)} autoFocus required disabled={submitting} />
        <label className="flex flex-col gap-1 text-sm text-text-secondary">
          Descrição
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            disabled={submitting}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
          />
        </label>
        <Input
          label="Etiquetas (separadas por vírgula)"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          disabled={submitting}
        />
        {error ? (
          <p role="alert" className="text-sm text-[#f08484]">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting || name.trim().length === 0}>
            {submitting ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
